import { BackgroundToken, baseColors, darkThemeColors, lightThemeColors, ThemeColors } from './colors'

// Aurora-specific theme extensions that don't exist in the base ThemeColors interface
interface AuroraIconTokens {
  spotIllustration: {
    primary: { fill: string }
    secondary: { fill: string }
    background: { fill: string }
    orbGradientStart: { fill: string }
    orbGradientEnd: { fill: string }
  }
  benefitIllustration: {
    primary: { fill: string }
    secondary: { fill: string }
    tertiary: { fill: string }
    light: { fill: string }
  }
}

interface AuroraComponentTokens {
  heroSection: { background: { gradient: readonly [string, string, ...string[]] } }
  contentSection: { background: { gradient: readonly [string, string, ...string[]] } }
  careOptionCard: {
    container: BackgroundToken
  }
  careNavigatorCard: {
    container: BackgroundToken
    phoneNumbersContainer: BackgroundToken
  }
  careNavigatorContactCard: {
    container: BackgroundToken
  }
  careOptionsCarousel: {
    cardShadow: { fill: string }
    cardShadowWithOpacity: { fill: string }
    dotsPagination: {
      activeDot: { fill: string }
      inactiveDot: { fill: string }
    }
  }
  compactTile: {
    container: BackgroundToken
  }
  toolkitPage: {
    headerBackground: BackgroundToken
  }
  favoritedProvidersRemainingDays: {
    warning: string
  }
  inlineInnovationBanner: {
    background: string
  }
  calendarNotificationBanner: {
    background: string
  }
}

export interface AuroraThemeColors extends ThemeColors {
  // Footer gradient base color (opacity applied in component)
  gradientBackgroundFooter: string

  // Input text selection
  inputSelectionText: string
  inputSelectionBackground: string

  icons: ThemeColors['icons'] & AuroraIconTokens
  components: ThemeColors['components'] & AuroraComponentTokens

  // Button variants
  primaryButtonCobaltLightBackgroundDefault: string
  primaryButtonCobaltLightBackgroundHover: string
  primaryButtonCobaltLightBackgroundPressed: string
  primaryButtonCobaltLightBackgroundInactive: string
  primaryButtonCobaltLightBackgroundLoading: string
  primaryButtonCobaltLightBackgroundFocus: string
  primaryButtonCobaltLightTextActive: string
  primaryButtonCobaltLightTextInactive: string
}

export const auroraBaseColors = {
  ...baseColors,

  // Core UI colors
  cobalt_80: '#06163B',
  cobalt_75: '#001442',
  cobalt_70: '#021B57',
  cobalt_65: '#02306B',
  cobalt_60: '#0E3C76',
  cobalt_50: '#1F579E',
  cobalt_45: '#315E9B',
  cobalt_40: '#3876CC',
  cobalt_35: '#599CF8',
  cobalt_30: '#7AB1FE',
  cobalt_25: '#A8CDFF',
  cobalt_20: '#CDE3FF',
  cobalt_15: '#E7F1FF',
  cobalt_10: '#F3FEFE',

  // Success
  green_45: '#3D701A',

  // Warning
  gold_70: '#782800',
  gold_60: '#8C351C',

  // Error
  red_70: '#82001B',

  // White opacity
  white_opacity_80: 'rgba(255, 255, 255, 0.8)',
  white_opacity_70: 'rgba(255, 255, 255, 0.7)',
  white_opacity_60: 'rgba(255, 255, 255, 0.6)',
  white_opacity_50: 'rgba(255, 255, 255, 0.5)',
  white_opacity_40: 'rgba(255, 255, 255, 0.4)',
  white_opacity_35: 'rgba(255, 255, 255, 0.35)',
  white_opacity_30: 'rgba(255, 255, 255, 0.3)',
  white_opacity_20: 'rgba(255, 255, 255, 0.2)',
  white_opacity_10: 'rgba(255, 255, 255, 0.1)',

  // Black opacity
  black_opacity_10: 'rgba(0, 0, 0, 0.1)',

  // Cobalt opacity
  cobalt_mid_opacity: 'rgba(31, 87, 158, 0.7)', // rgba(31, 87, 158) = cobalt_50
  cobalt_high_opacity: 'rgba(31, 87, 158, 0.5)',
  cobalt_opacity_20: 'rgba(31, 87, 158, 0.2)',
  cobalt_light_opacity: 'rgba(227, 240, 255, 0.5)',
  cobalt_65_opacity_60: 'rgba(2, 48, 107, 0.6)',
  cobalt_70_opacity_20: 'rgba(2, 27, 87, 0.2)',
  cobalt_70_opacity_80: 'rgba(2, 27, 87, 0.8)',

  // Gradient background
  gradient_background_blend: '#E1EEFF',

  // Used for skeleton bone color - designs use a transparent white but the skeleton component needs a solid color to render the highlight gradient properly
  cobalt_60_white_opacity_50_blend: '#879DBB',

  // Inactive
  button_background_inactive: 'rgba(6, 22, 59, 0.5)',

  // Spot illustration colors
  blue_50: '#2E69BB',
  blue_40: '#5394ED',
  blue_30: '#8DBDFF',
  orange_50: '#FF7700',
  orange_40: '#FF9800',
  orange_30: '#FFB74D',
  yellow_40: '#FFD971',
  yellow_30: '#FFD54F',
  yellow_20: '#FFECB3',

  // Navbar
  header_light_end: 'rgba(129, 181, 254, 0.6)',
  header_dark_end: 'rgba(4, 33, 93, 0.6)',

  // Light mode gradient base color for footer
  // Dark mode footer gradient uses existing cobalt_50 (#1F579E)
  footer_gradient_light: '#BAD7FE',

  // Sweeping glow highlight applied over header text (GlowTextWrapper)
  glow_text_sweep: '#FFBB00',
}

export const auroraLightThemeColors: AuroraThemeColors = {
  ...lightThemeColors,

  // Text
  textPrimary: auroraBaseColors.cobalt_80,
  textSecondary: auroraBaseColors.cobalt_60,
  textActive: auroraBaseColors.cobalt_80,
  textSuccess: auroraBaseColors.green_70,
  textSuccessAlternate: auroraBaseColors.green_70,
  // lightest palette step that passes 4.5:1 on the cobalt_20 page background
  textMuted: auroraBaseColors.cobalt_45,
  textInactive: auroraBaseColors.cobalt_high_opacity,
  textWarning: auroraBaseColors.gold_70,
  textError: auroraBaseColors.red_70,
  textButton: auroraBaseColors.white,
  textInverse: auroraBaseColors.white,
  textInverseSecondary: auroraBaseColors.white_opacity_70,

  // Link
  linkDefault: auroraBaseColors.cobalt_80,
  linkHover: auroraBaseColors.cobalt_60,
  linkInactive: auroraBaseColors.cobalt_high_opacity,

  // Background
  backgroundTooltip: auroraBaseColors.cobalt_60,
  backgroundPrimary: auroraBaseColors.cobalt_20,
  backgroundSecondary: auroraBaseColors.cobalt_25,
  backgroundSection: auroraBaseColors.white_opacity_20,
  backgroundHighlightPrimary: auroraBaseColors.blueberry_10,
  backgroundHighlightTertiary: auroraBaseColors.peri_10,
  backgroundHighlightSecondary: auroraBaseColors.peach_10,
  backgroundTransparent: auroraBaseColors.transparent,
  backgroundActive: auroraBaseColors.cobalt_40,
  backgroundHover: auroraBaseColors.cobalt_50,
  backgroundPressed: auroraBaseColors.cobalt_60,
  backgroundLoading: auroraBaseColors.cobalt_60,
  backgroundFocus: auroraBaseColors.cobalt_20,
  backgroundSuccess: auroraBaseColors.green_10,
  backgroundWarning: auroraBaseColors.gold_10,
  backgroundError: auroraBaseColors.red_10,
  backgroundElement: auroraBaseColors.white_opacity_30,
  backgroundHeaderPrimary: auroraBaseColors.cobalt_10,
  backgroundHeaderSecondary: auroraBaseColors.cobalt_10,
  backgroundNavHeader: auroraBaseColors.transparent,
  backgroundPhoto: auroraBaseColors.cobalt_40,
  backgroundTopMatch: auroraBaseColors.cobalt_20,
  backgroundDarkOverlay: auroraBaseColors.black,
  backgroundModal: auroraBaseColors.cobalt_15,
  backgroundSurfaceElement: auroraBaseColors.white_opacity_40,
  backgroundDropdown: auroraBaseColors.cobalt_15,
  backgroundPrimaryDark: auroraBaseColors.cobalt_60,

  // Border
  borderDefault: auroraBaseColors.white_opacity_30,
  borderActive: auroraBaseColors.white,
  borderHover: auroraBaseColors.white,
  borderFocus: auroraBaseColors.cobalt_80,
  borderInput: auroraBaseColors.cobalt_50,
  borderSuccess: auroraBaseColors.green_70,
  borderWarning: auroraBaseColors.gold_70,
  borderError: auroraBaseColors.red_70,
  borderTopMatch: auroraBaseColors.cobalt_20,

  // Icon
  iconDefault: auroraBaseColors.cobalt_80,
  iconActive: auroraBaseColors.cobalt_50,
  iconSuccess: auroraBaseColors.green_70,
  iconWarning: auroraBaseColors.gold_70,
  iconError: auroraBaseColors.red_70,
  iconAccent: auroraBaseColors.gold_40,

  // Divider
  dividerPrimary: auroraBaseColors.cobalt_high_opacity,
  dividerSecondary: auroraBaseColors.cobalt_mid_opacity,

  // Primary button
  primaryButtonBackgroundDefault: auroraBaseColors.cobalt_80,
  primaryButtonBackgroundHover: auroraBaseColors.cobalt_70,
  primaryButtonBackgroundPressed: auroraBaseColors.cobalt_70,
  primaryButtonBackgroundInactive: auroraBaseColors.button_background_inactive,
  primaryButtonBackgroundLoading: auroraBaseColors.cobalt_70,
  primaryButtonBackgroundFocus: auroraBaseColors.cobalt_70,
  primaryButtonTextActive: auroraBaseColors.white,
  primaryButtonTextInactive: auroraBaseColors.white_opacity_50,

  // Primary button variants
  primaryButtonCobaltLightBackgroundDefault: auroraBaseColors.cobalt_20,
  primaryButtonCobaltLightBackgroundHover: auroraBaseColors.cobalt_30,
  primaryButtonCobaltLightBackgroundPressed: auroraBaseColors.cobalt_30,
  primaryButtonCobaltLightBackgroundInactive: auroraBaseColors.cobalt_40,
  primaryButtonCobaltLightBackgroundLoading: auroraBaseColors.cobalt_30,
  primaryButtonCobaltLightBackgroundFocus: auroraBaseColors.cobalt_30,
  primaryButtonCobaltLightTextActive: auroraBaseColors.cobalt_80,
  primaryButtonCobaltLightTextInactive: auroraBaseColors.cobalt_60,

  // Secondary button
  secondaryButtonBackgroundDefault: auroraBaseColors.white_opacity_40,
  secondaryButtonBackgroundHover: auroraBaseColors.white_opacity_50,
  secondaryButtonBackgroundPressed: auroraBaseColors.white_opacity_50,
  secondaryButtonBackgroundInactive: auroraBaseColors.white_opacity_10,
  secondaryButtonBackgroundFocus: auroraBaseColors.white_opacity_50,
  secondaryButtonOutlineDefault: auroraBaseColors.white_opacity_30,
  secondaryButtonOutlineInactive: auroraBaseColors.transparent,
  secondaryButtonTextActive: auroraBaseColors.cobalt_80,
  secondaryButtonTextInactive: auroraBaseColors.button_background_inactive,

  // Secondary button variants
  secondaryButtonInverseBackgroundDefault: auroraBaseColors.transparent,
  secondaryButtonInverseBackgroundHover: auroraBaseColors.blueberry_40,
  secondaryButtonInverseBackgroundPressed: auroraBaseColors.blueberry_50,
  secondaryButtonInverseBackgroundFocus: auroraBaseColors.blueberry_40,
  secondaryButtonInverseOutlineDefault: auroraBaseColors.blueberry_20,
  secondaryButtonInverseOutlinePressed: auroraBaseColors.blueberry_20,
  secondaryButtonInverseOutlineInactive: auroraBaseColors.oatmeal_40,
  secondaryButtonInverseTextActive: auroraBaseColors.blueberry_20,
  secondaryButtonInverseTextInactive: auroraBaseColors.oatmeal_40,

  // Tertiary button (icon button)
  tertiaryButtonBackgroundDefault: auroraBaseColors.transparent,
  tertiaryButtonBackgroundHover: auroraBaseColors.cobalt_light_opacity,
  tertiaryButtonBackgroundPressed: auroraBaseColors.cobalt_light_opacity,
  tertiaryButtonBackgroundInactive: auroraBaseColors.transparent,
  tertiaryButtonBackgroundFocus: auroraBaseColors.cobalt_light_opacity,
  tertiaryButtonTextActive: auroraBaseColors.cobalt_80,
  tertiaryButtonTextInactive: auroraBaseColors.button_background_inactive,

  // Secondary Tertiary button (icon button) tokens
  secondaryTertiaryButtonBackgroundDefault: auroraBaseColors.white_opacity_40,
  secondaryTertiaryButtonBackgroundHover: auroraBaseColors.white_opacity_50,
  secondaryTertiaryButtonBackgroundPressed: auroraBaseColors.white_opacity_50,
  secondaryTertiaryButtonBackgroundInactive: auroraBaseColors.white_opacity_10,
  secondaryTertiaryButtonBackgroundFocus: auroraBaseColors.white_opacity_50,

  // Secondary icon button tokens
  secondaryIconButtonBackgroundDefault: auroraBaseColors.white_opacity_40,
  secondaryIconButtonBackgroundHover: auroraBaseColors.white_opacity_50,
  secondaryIconButtonBackgroundPressed: auroraBaseColors.white_opacity_50,
  secondaryIconButtonBackgroundInactive: auroraBaseColors.white_opacity_10,
  secondaryIconButtonBackgroundFocus: auroraBaseColors.white_opacity_50,
  secondaryIconButtonOutlineDefault: auroraBaseColors.white_opacity_30,

  // Teal icon button tokens
  tealIconButtonBackgroundDefault: auroraBaseColors.white_opacity_10,
  tealIconButtonBackgroundHover: auroraBaseColors.white_opacity_10,
  tealIconButtonBackgroundPressed: auroraBaseColors.cobalt_60,
  tealIconButtonBackgroundFocus: auroraBaseColors.cobalt_60,
  tealIconButtonIconDefault: auroraBaseColors.white,
  tealIconButtonIconHover: auroraBaseColors.white,
  tealIconButtonIconPressed: auroraBaseColors.white,
  tealIconButtonIconFocus: auroraBaseColors.white,

  // Primary icon button tokens
  primaryIconButtonBackgroundDefault: auroraBaseColors.transparent,
  primaryIconButtonBackgroundHover: auroraBaseColors.white_opacity_20,
  primaryIconButtonBackgroundPressed: auroraBaseColors.white_opacity_20,
  primaryIconButtonBackgroundFocus: auroraBaseColors.white_opacity_20,
  primaryIconButtonBackgroundInactive: auroraBaseColors.transparent,
  primaryIconButtonTextActive: auroraBaseColors.cobalt_80,

  // Word cloud button
  wordCloudButtonBackgroundDefault: auroraBaseColors.white_opacity_30,
  wordCloudButtonBackgroundPressed: auroraBaseColors.white_opacity_30,
  wordCloudButtonBackgroundSelected: auroraBaseColors.white,
  wordCloudButtonOutlineDefault: auroraBaseColors.white_opacity_40,
  wordCloudButtonOutlineHover: auroraBaseColors.white_opacity_60,
  wordCloudButtonOutlinePressed: auroraBaseColors.white,
  wordCloudButtonOutlineInactive: auroraBaseColors.transparent,
  wordCloudButtonOutlineSelected: auroraBaseColors.white,
  wordCloudButtonTextActive: auroraBaseColors.cobalt_80,
  wordCloudButtonTextInactive: auroraBaseColors.cobalt_mid_opacity,
  wordCloudButtonTextPressed: auroraBaseColors.white_opacity_80,
  wordCloudButtonTextSelected: auroraBaseColors.cobalt_80,

  // Carousel button
  carouselButtonFilledBackgroundActive: auroraBaseColors.cobalt_40,
  carouselButtonFilledBackgroundHover: auroraBaseColors.cobalt_50,
  carouselButtonFilledBackgroundPressed: auroraBaseColors.cobalt_60,
  carouselButtonFilledBackgroundFocus: auroraBaseColors.cobalt_50,

  // Input field
  inputOutlineDefault: auroraBaseColors.cobalt_50,
  inputOutlineSelected: auroraBaseColors.cobalt_50,
  inputOutlineInactive: auroraBaseColors.transparent,
  inputOutlineHover: auroraBaseColors.cobalt_60,
  inputOutlineFocus: auroraBaseColors.cobalt_60,
  inputOutlineError: auroraBaseColors.red_70,
  inputTextDefault: auroraBaseColors.cobalt_80,
  inputTextPlaceholder: auroraBaseColors.cobalt_60,
  inputTextInactive: auroraBaseColors.cobalt_60,
  inputTextSuccess: auroraBaseColors.green_70,
  inputTextWarning: auroraBaseColors.gold_70,
  inputTextError: auroraBaseColors.red_70,
  inputSelectionText: auroraBaseColors.cobalt_80,
  inputSelectionBackground: auroraBaseColors.white_opacity_80,
  inputBackgroundDefault: auroraBaseColors.transparent,
  inputBackgroundInactive: auroraBaseColors.white_opacity_20,
  inputBackgroundTriageDefault: auroraBaseColors.white_opacity_30,
  inputOutlineTriageDefault: auroraBaseColors.white_opacity_40,

  // Typeahead
  typeaheadMenuBackground: auroraBaseColors.cobalt_20,
  typeaheadItemBackgroundHover: auroraBaseColors.cobalt_light_opacity,

  // Checkbox
  checkboxBackgroundPrimary: auroraBaseColors.transparent,
  checkboxBackgroundPrimarySelected: auroraBaseColors.blueberry_10,
  checkboxBackgroundSelected: auroraBaseColors.transparent,
  checkboxBackgroundPressed: auroraBaseColors.cobalt_40,
  checkboxOutlineSelected: auroraBaseColors.cobalt_40,
  checkboxOutlineDefault: auroraBaseColors.cobalt_80,
  checkboxOutlineHovered: auroraBaseColors.cobalt_40,
  checkboxCheckSelected: auroraBaseColors.cobalt_80,
  checkboxOutlineInactive: auroraBaseColors.cobalt_mid_opacity,
  checkboxTextDefault: baseColors.oatmeal_60,
  checkboxTextSelected: auroraBaseColors.cobalt_80,

  // Radio
  radioBackgroundPrimary: auroraBaseColors.transparent,
  radioBackgroundSelected: auroraBaseColors.cobalt_80,
  radioBackgroundSelectedInactive: auroraBaseColors.cobalt_mid_opacity,
  radioTextDefault: auroraBaseColors.cobalt_80,
  radioTextHighlight: auroraBaseColors.cobalt_80,
  radioTextSelected: auroraBaseColors.cobalt_80,
  radioOutlineDefault: auroraBaseColors.cobalt_80,
  radioOutlineHover: auroraBaseColors.cobalt_80,
  radioOutlineInactive: auroraBaseColors.cobalt_mid_opacity,
  radioOutlineSelected: auroraBaseColors.cobalt_80,
  radioOutlineSelectedInactive: auroraBaseColors.cobalt_mid_opacity,
  radioOutlineFocused: auroraBaseColors.white,

  // Radio with outline
  radioWithOutlineBackgroundSelected: auroraBaseColors.white_opacity_80,
  radioWithOutlineBorderSelected: auroraBaseColors.white,
  radioWithOutlinePrimaryTextSelected: auroraBaseColors.cobalt_80,
  radioWithOutlineSecondaryTextSelected: auroraBaseColors.cobalt_80,
  radioWithOutlineBorderHovered: auroraBaseColors.white_opacity_60,
  radioWithOutlinePrimaryTextInactive: auroraBaseColors.cobalt_mid_opacity,
  radioWithOutlineSecondaryTextInactive: auroraBaseColors.cobalt_mid_opacity,
  radioWithOutlineBackgroundDefault: auroraBaseColors.white_opacity_30,
  radioWithOutlineBorderInactive: auroraBaseColors.transparent,
  radioWithOutlinePrimaryTextDefault: auroraBaseColors.cobalt_80,
  radioWithOutlineSecondaryTextDefault: auroraBaseColors.cobalt_80,
  radioWithOutlineBorderDefault: auroraBaseColors.white_opacity_40,
  radioWithOutlineBackgroundPressed: auroraBaseColors.white_opacity_60,

  // Cards
  cardBackgroundPrimary: auroraBaseColors.white_opacity_40,
  cardBackgroundSecondary: auroraBaseColors.white_opacity_40,
  cardOutlinePrimary: auroraBaseColors.transparent,
  cardOutlineSecondary: auroraBaseColors.transparent,
  cardOutlineInactive: auroraBaseColors.transparent,
  topMatchCardGradientStart: auroraBaseColors.cobalt_80,
  topMatchCardGradientEnd: auroraBaseColors.cobalt_60,
  cardBoxShadow: auroraBaseColors.black_opacity_10,

  // Progress indicator
  progressIndicator: auroraBaseColors.cobalt_50,
  progressIndicatorBackgroundTop: auroraBaseColors.white_opacity_30,
  progressIndicatorBackgroundBottom: auroraBaseColors.white_opacity_30,

  // ScrollView Refresher
  scrollViewRefresherLoadingIcon: auroraBaseColors.cobalt_30,

  // Gradient Tokens
  gradientBubbleStart: auroraBaseColors.cobalt_10,
  gradientHeaderStart: auroraBaseColors.cobalt_30,
  gradientHeaderEnd: auroraBaseColors.header_light_end,

  // Page background gradient tokens
  backgroundGradientFirst: auroraBaseColors.cobalt_30,
  backgroundGradientSecond: auroraBaseColors.cobalt_20,

  // Footer gradient (opacity applied in AuroraFooterWrapper: 0%, 90%, 100%)
  gradientBackgroundFooter: auroraBaseColors.footer_gradient_light,

  // AI Powered Pill
  aiPoweredText: auroraBaseColors.cobalt_50,

  // Skeleton tokens

  components: {
    ...lightThemeColors.components,

    badge: {
      ...lightThemeColors.components.badge,
      success: {
        background: auroraBaseColors.green_10,
        text: auroraBaseColors.green_45,
      },
      attention: {
        background: auroraBaseColors.gold_10,
        text: auroraBaseColors.gold_50,
      },
      alert: {
        background: auroraBaseColors.red_10,
        text: auroraBaseColors.red_50,
      },
      infoPrimary: {
        background: auroraBaseColors.white,
        text: auroraBaseColors.cobalt_60,
      },
      inverse: {
        background: auroraBaseColors.cobalt_20,
        text: auroraBaseColors.cobalt_80,
      },
      neutral: {
        background: auroraBaseColors.white_opacity_70,
        text: auroraBaseColors.cobalt_60,
      },
    },
    googlePlacesField: {
      textInput: {
        background: lightThemeColors.backgroundTransparent,
        text: auroraBaseColors.cobalt_60,
      },
      row: {
        background: lightThemeColors.backgroundTransparent,
        text: auroraBaseColors.cobalt_60,
      },
    },
    activityHeader: {
      headline: {
        border: auroraBaseColors.cobalt_30,
      },
    },
    alertBanner: {
      ...lightThemeColors.components.alertBanner,
      calm: { background: auroraBaseColors.cobalt_60, text: auroraBaseColors.white },
      neutral: { background: auroraBaseColors.white, text: auroraBaseColors.cobalt_60 },
    },
    avatar: {
      ...lightThemeColors.components.avatar,
      tealTheme: { background: auroraBaseColors.cobalt_10, text: auroraBaseColors.cobalt_50 },
      defaultAvatarTextColor: auroraBaseColors.cobalt_45,
    },
    chatBubble: {
      background: auroraBaseColors.white,
      border: auroraBaseColors.oatmeal_20,
      outOfOffice: {
        background: auroraBaseColors.cobalt_40,
        text: auroraBaseColors.white,
      },
    },
    coachMark: {
      background: auroraBaseColors.cobalt_60,
      arrow: {
        border: auroraBaseColors.cobalt_60,
      },
    },
    compactTile: {
      container: { background: auroraBaseColors.white_opacity_40 },
    },
    dateBlock: {
      border: auroraBaseColors.cobalt_40,
      text: auroraBaseColors.cobalt_40,
      background: auroraBaseColors.cobalt_10,
    },
    favoritedProvidersStarIcon: {
      ...lightThemeColors.components.favoritedProvidersStarIcon,
      filledAlternate: {
        fill: auroraBaseColors.cobalt_80,
      },
      unfilled: {
        fill: auroraBaseColors.cobalt_80,
      },
    },
    favoritedProvidersRemainingDays: {
      warning: auroraBaseColors.gold_60,
    },
    feedbackStarIcon: {
      ...lightThemeColors.components.feedbackStarIcon,
      default: {
        ...lightThemeColors.components.feedbackStarIcon.default,
        border: auroraBaseColors.cobalt_50,
        hover: {
          fill: auroraBaseColors.white,
          border: auroraBaseColors.cobalt_50,
        },
        highlighted: {
          fill: auroraBaseColors.cobalt_50,
          border: auroraBaseColors.cobalt_50,
        },
      },
    },
    floatingFooterBackground: {
      background: auroraBaseColors.white_opacity_50,
    },
    floatingFooterMobileBackground: {
      background: auroraBaseColors.gradient_background_blend,
    },
    focusBoxShadow: {
      outerBox: { fill: auroraBaseColors.white },
      innerBox: { fill: auroraBaseColors.cobalt_30 },
    },
    genericHeader: {
      background: auroraBaseColors.cobalt_60,
    },
    integratedBenefitsEntryCard: {
      fencepost: {
        border: auroraBaseColors.cobalt_opacity_20,
      },
    },
    lessonPreview: {
      ...lightThemeColors.components.lessonPreview,
      progressBar: {
        fill: auroraBaseColors.cobalt_40,
      },
    },
    lessonThumbnail: {
      iconDisplay: {
        background: auroraBaseColors.white,
        fill: auroraBaseColors.cobalt_50,
      },
      disabledIcon: {
        fill: auroraBaseColors.cobalt_30,
      },
      progressBar: {
        fill: auroraBaseColors.cobalt_40,
      },
    },
    locationSearchNavigation: {
      ...lightThemeColors.components.locationSearchNavigation,
      highlightColor: { background: lightThemeColors.backgroundHighlightSecondary },
      separatorColor: { background: auroraBaseColors.transparent },
      infoBadge: {
        background: auroraBaseColors.white_opacity_70,
        text: auroraBaseColors.cobalt_60,
      },
    },
    onsiteLocation: {
      badge: {
        background: auroraBaseColors.cobalt_10,
        text: auroraBaseColors.cobalt_40,
      },
    },
    messageAttachment: {
      innerContainer: {
        background: auroraBaseColors.cobalt_50,
      },
    },
    messageItem: {
      ...lightThemeColors.components.messageItem,
      chatBubbleSender: {
        background: auroraBaseColors.cobalt_50,
        border: auroraBaseColors.cobalt_50,
      },
      chatBubbleAurora: {
        background: auroraBaseColors.white_opacity_10,
        text: auroraBaseColors.white,
      },
      chatBubbleReceiverAi: {
        ...lightThemeColors.components.messageItem.chatBubbleReceiverAi,
        gradientStart: { background: auroraBaseColors.white_opacity_10 },
        gradientEnd: { background: auroraBaseColors.white_opacity_10 },
        text: auroraBaseColors.cobalt_80,
      },
    },
    chatInput: {
      ...lightThemeColors.components.chatInput,
      sendButtonBackgroundActive: auroraBaseColors.white_opacity_20,
      sendButtonBackgroundInactive: auroraBaseColors.white_opacity_10,
      sendButtonIconActive: auroraBaseColors.white,
      sendButtonIconInactive: auroraBaseColors.white_opacity_30,
    },
    aiStatusBadge: {
      ...lightThemeColors.components.aiStatusBadge,
      processing: {
        background: auroraBaseColors.cobalt_10,
        text: auroraBaseColors.cobalt_50,
      },
    },
    notificationsOverview: {
      notificationIcon: {
        fill: auroraBaseColors.cobalt_60,
      },
    },
    passwordChecklist: {
      ...lightThemeColors.components.passwordChecklist,
      checklistBullet: {
        fill: auroraBaseColors.cobalt_80,
      },
    },
    platformEngagementQuarterlyDetails: {
      text: auroraBaseColors.cobalt_60,
    },
    privacyScreen: {
      textContainer: { background: auroraBaseColors.cobalt_60 },
    },
    profilePageCard: { border: auroraBaseColors.transparent },
    progressBar: {
      ...lightThemeColors.components.progressBar,
      fill: auroraBaseColors.cobalt_20,
    },
    progressBarSecondary: {
      fill: auroraBaseColors.cobalt_30,
      background: auroraBaseColors.cobalt_20,
    },
    heroSection: {
      background: {
        gradient: [auroraBaseColors.cobalt_70, auroraBaseColors.cobalt_50] as const,
      },
    },
    contentSection: {
      background: {
        gradient: [auroraBaseColors.cobalt_30, auroraBaseColors.cobalt_25] as const,
      },
    },
    providerCalendar: {
      ...lightThemeColors.components.providerCalendar,
      event: {
        ...lightThemeColors.components.providerCalendar.event,
        background: {
          background: auroraBaseColors.cobalt_20,
          border: auroraBaseColors.cobalt_20,
        },
        bookableHover: {
          background: auroraBaseColors.cobalt_10,
          border: auroraBaseColors.cobalt_40,
        },
        recurringSession: {
          background: auroraBaseColors.cobalt_20,
          border: auroraBaseColors.cobalt_20,
          text: auroraBaseColors.cobalt_60,
        },
        recurringSessionPast: {
          background: auroraBaseColors.cobalt_10,
          border: auroraBaseColors.cobalt_10,
          text: auroraBaseColors.cobalt_50,
        },
        mirror: {
          background: auroraBaseColors.cobalt_10,
          border: auroraBaseColors.cobalt_40,
        },
      },
    },
    providerLanguagesSpoken: {
      ...lightThemeColors.components.providerLanguagesSpoken,
      textBadge: {
        text: auroraBaseColors.cobalt_50,
      },
    },
    providerBadges: {
      ...lightThemeColors.components.providerBadges,
      bioKeywordBadge: {
        background: auroraBaseColors.white_opacity_70,
        text: auroraBaseColors.cobalt_50,
      },
      preferenceMatchBadge: {
        checkIcon: { fill: auroraBaseColors.cobalt_30 },
        background: auroraBaseColors.white_opacity_20,
        popoverBackground: auroraBaseColors.cobalt_50,
      },
      specializationBadge: {
        background: auroraBaseColors.white_opacity_70,
        text: auroraBaseColors.cobalt_50,
      },
    },
    preferenceMatchCheckmark: {
      background: auroraBaseColors.cobalt_80,
      foreground: auroraBaseColors.white,
    },
    s4cLoadingIndicator: {
      text: auroraBaseColors.cobalt_60,
    },
    s4cHeader: {
      background: auroraBaseColors.cobalt_10,
    },
    sliderField: {
      text: auroraBaseColors.cobalt_50,
      thumb: {
        ...lightThemeColors.components.sliderField.thumb,
        border: auroraBaseColors.cobalt_40,
      },
      track: {
        ...lightThemeColors.components.sliderField.track,
        minimumTint: {
          background: auroraBaseColors.cobalt_40,
        },
      },
      stepMarker: {
        ...lightThemeColors.components.sliderField.stepMarker,
        filled: {
          background: auroraBaseColors.cobalt_10,
        },
      },
      floatingLabel: {
        ...lightThemeColors.components.sliderField.floatingLabel,
        border: auroraBaseColors.cobalt_40,
      },
    },
    multicolorSliderField: {
      ...lightThemeColors.components.multicolorSliderField,
      text: auroraBaseColors.cobalt_50,
    },
    dotsPagination: {
      ...lightThemeColors.components.dotsPagination,
      activeDot: {
        fill: auroraBaseColors.cobalt_30,
      },
    },
    triageSummary: {
      ...lightThemeColors.components.triageSummary,
      badge: {
        background: auroraBaseColors.cobalt_20,
        fill: auroraBaseColors.cobalt_50,
      },
    },
    toolbar: {
      background: auroraBaseColors.cobalt_60,
    },
    toolkitPage: {
      headerBackground: { background: auroraBaseColors.cobalt_25 },
    },
    treatmentCard: {
      ...lightThemeColors.components.treatmentCard,
      findCounsellorSection: {
        icon: {
          fill: auroraBaseColors.cobalt_60,
        },
      },
      headlineUnderline: {
        background: auroraBaseColors.white_opacity_50,
      },
    },
    unsupportedLanguageDisclaimer: {
      text: auroraBaseColors.cobalt_50,
    },
    utilityItemBadge: {
      ...lightThemeColors.components.utilityItemBadge,
      fill: auroraBaseColors.cobalt_50,
    },
    videoControlButton: {
      ...lightThemeColors.components.videoControlButton,
      disabledColor: {
        fill: auroraBaseColors.cobalt_30,
      },
    },
    videoLessonComplete: {
      ...lightThemeColors.components.videoLessonComplete,
      check: { fill: auroraBaseColors.cobalt_20 },
      completeText: { text: auroraBaseColors.cobalt_20 },
    },
    videoPlayer: {
      ...lightThemeColors.components.videoPlayer,
      minimumTrackTint: { background: auroraBaseColors.cobalt_20 },
    },
    welcomeSplashScreen: {
      backButton: { fill: auroraBaseColors.cobalt_60 },
    },
    wellnessToolkitEssentials: {
      text: auroraBaseColors.cobalt_80,
    },
    zoomParticipantStatus: {
      ...lightThemeColors.components.zoomParticipantStatus,
      ready: {
        ...lightThemeColors.components.zoomParticipantStatus.ready,
        background: auroraBaseColors.cobalt_40,
      },
    },
    zoomVideoControls: {
      ...lightThemeColors.components.zoomVideoControls,
      disabled: { background: auroraBaseColors.cobalt_40 },
      end: { background: baseColors.red_50 },
    },
    searchBar: {
      container: { background: auroraBaseColors.white_opacity_20 },
    },
    floatingMobileNav: {
      background: auroraBaseColors.white_opacity_80,
      iconActive: auroraBaseColors.cobalt_50,
      iconDefault: auroraBaseColors.cobalt_80,
      activeIndicator: auroraBaseColors.cobalt_50,
    },
    languageSelector: {
      button: { background: auroraBaseColors.white_opacity_40, border: auroraBaseColors.white_opacity_20 },
    },
    segmentedToggle: {
      container: { background: auroraBaseColors.white_opacity_30, border: auroraBaseColors.white_opacity_40 },
      option: { text: auroraBaseColors.cobalt_80 },
      optionHovered: { background: auroraBaseColors.white_opacity_60 },
      optionSelected: {
        background: auroraBaseColors.white,
        border: auroraBaseColors.white_opacity_30,
        text: auroraBaseColors.cobalt_80,
      },
    },
    chatHeader: {
      ...lightThemeColors.components.chatHeader,
      background: { background: auroraBaseColors.transparent },
      backgroundScrolled: { background: auroraBaseColors.cobalt_65_opacity_60 },
    },
    careNavigatorContactCard: {
      container: { background: auroraBaseColors.white_opacity_40 },
    },
    careNavigatorHeaderEntryPointButton: {
      iconCircle: {
        background: auroraBaseColors.white_opacity_40,
        border: auroraBaseColors.white_opacity_30,
        lightBackground: auroraBaseColors.cobalt_20,
        lightBorder: auroraBaseColors.white,
      },
      icon: { fill: auroraBaseColors.cobalt_80 },
      heart: { fill: auroraBaseColors.cobalt_80 },
    },
    careOptionsCarousel: {
      cardShadow: { fill: auroraBaseColors.cobalt_70 },
      cardShadowWithOpacity: { fill: auroraBaseColors.cobalt_70_opacity_20 },
      dotsPagination: {
        activeDot: {
          fill: auroraBaseColors.cobalt_60,
        },
        inactiveDot: {
          fill: auroraBaseColors.cobalt_20,
        },
      },
    },
    pathwaysFAQSection: {
      overlay: { background: auroraBaseColors.white_opacity_20 },
    },
    careNavigatorCard: {
      container: {
        background: auroraBaseColors.white_opacity_10,
      },
      phoneNumbersContainer: {
        background: auroraBaseColors.cobalt_60,
      },
    },
    careOptionCard: {
      container: { background: auroraBaseColors.cobalt_20 },
    },
    twoColumnRegistrationLayout: {
      formBackground: {
        background: auroraBaseColors.transparent,
      },
      ssoDivider: {
        background: auroraBaseColors.cobalt_mid_opacity,
      },
    },
    inlineInnovationBanner: {
      background: auroraBaseColors.white,
      text: auroraBaseColors.cobalt_60,
    },
    calendarNotificationBanner: {
      background: auroraBaseColors.cobalt_40,
    },
  },
  icons: {
    ...lightThemeColors.icons,

    appIcon: {
      ...lightThemeColors.icons.appIcon,
      background: auroraBaseColors.cobalt_60,
    },
    avatarIcon: {
      body: { fill: auroraBaseColors.cobalt_10 },
      shadow: { fill: auroraBaseColors.cobalt_20 },
      fill: auroraBaseColors.cobalt_30,
    },
    checkMarkIcon: {
      fill: auroraBaseColors.cobalt_30,
    },
    essentialsIcon: {
      fill: auroraBaseColors.cobalt_60,
    },
    eyeIcon: {
      fill: auroraBaseColors.cobalt_80,
    },
    fingerprintIcon: {
      fill: auroraBaseColors.cobalt_20,
    },
    linkIcon: {
      chain: { fill: auroraBaseColors.cobalt_30 },
    },
    liveChatMessagingIcon: {
      fill: auroraBaseColors.cobalt_10,
    },
    logoutIcon: {
      rectangle: { fill: auroraBaseColors.cobalt_20 },
      arrow: { fill: auroraBaseColors.cobalt_40 },
    },
    lyraLogoPrimaryIcon: {
      fill: auroraBaseColors.cobalt_80,
    },
    lyraLogoAppPromoIcon: {
      fill: auroraBaseColors.cobalt_70,
    },
    lyraHealthAppIcon: {
      background: { fill: auroraBaseColors.lyra_app_blue },
      logo: { fill: auroraBaseColors.white },
    },
    menuIcon: {
      fill: auroraBaseColors.cobalt_60,
    },
    messagingIcon: {
      border: auroraBaseColors.cobalt_60,
    },
    notificationIcon: {
      fill: auroraBaseColors.cobalt_30,
    },
    progressIcon: {
      fill: auroraBaseColors.cobalt_60,
    },
    scoringIcon: {
      ...lightThemeColors.icons.scoringIcon,
      neutral: {
        arrow: { fill: auroraBaseColors.cobalt_40 },
        stroke: { fill: auroraBaseColors.cobalt_20 },
        fill: auroraBaseColors.cobalt_10,
      },
    },
    selfCareIcon: {
      ...lightThemeColors.icons.selfCareIcon,
      fill: auroraBaseColors.cobalt_30,
    },
    sessionIcon: {
      fill: auroraBaseColors.cobalt_60,
    },
    timeIcon: {
      background: auroraBaseColors.cobalt_10,
      clockHandBackground: {
        fill: auroraBaseColors.cobalt_20,
      },
      clockHandForeground: {
        fill: auroraBaseColors.cobalt_30,
      },
    },
    xIcon: {
      fill: auroraBaseColors.cobalt_80,
    },
    spotIllustration: {
      primary: { fill: auroraBaseColors.blue_40 },
      secondary: { fill: auroraBaseColors.blue_50 },
      background: { fill: auroraBaseColors.white_opacity_40 },
      orbGradientStart: { fill: auroraBaseColors.yellow_40 },
      orbGradientEnd: { fill: auroraBaseColors.orange_50 },
    },
    benefitIllustration: {
      primary: { fill: auroraBaseColors.blue_40 },
      secondary: { fill: auroraBaseColors.blue_50 },
      tertiary: { fill: auroraBaseColors.cobalt_20 },
      light: { fill: auroraBaseColors.blue_30 },
    },
  },
}

// TODO Update dark theme colors
export const auroraDarkThemeColors: AuroraThemeColors = {
  ...auroraLightThemeColors,
  ...darkThemeColors,

  // Text
  textPrimary: auroraBaseColors.white,
  textSecondary: auroraBaseColors.cobalt_20,
  textActive: auroraBaseColors.white,
  textSuccess: auroraBaseColors.green_20,
  textSuccessAlternate: auroraBaseColors.green_20,
  textMuted: auroraBaseColors.cobalt_30,
  textInactive: auroraBaseColors.white_opacity_40,
  textWarning: auroraBaseColors.gold_20,
  textError: auroraBaseColors.red_20,
  textButton: auroraBaseColors.cobalt_80,
  textInverse: auroraBaseColors.cobalt_80,

  // Link
  linkDefault: auroraBaseColors.white,
  linkHover: auroraBaseColors.cobalt_20,
  linkInactive: auroraBaseColors.white_opacity_40,

  // Background
  backgroundPrimary: auroraBaseColors.cobalt_60,
  backgroundSecondary: auroraBaseColors.cobalt_65,
  backgroundSection: auroraBaseColors.white_opacity_10,
  backgroundHighlightPrimary: auroraBaseColors.cobalt_80,
  backgroundHighlightSecondary: auroraBaseColors.cobalt_75,
  backgroundActive: auroraBaseColors.cobalt_30,
  backgroundHover: auroraBaseColors.cobalt_50,
  backgroundPressed: auroraBaseColors.cobalt_60,
  backgroundLoading: auroraBaseColors.cobalt_60,
  backgroundFocus: auroraBaseColors.cobalt_50,
  backgroundInactive: auroraBaseColors.white_opacity_30,
  backgroundTooltip: auroraBaseColors.cobalt_10,
  backgroundElement: auroraBaseColors.white_opacity_20,
  backgroundIcon: auroraBaseColors.cobalt_70,
  backgroundHeaderPrimary: auroraBaseColors.cobalt_75,
  backgroundHeaderSecondary: auroraBaseColors.cobalt_70,
  backgroundNavHeader: auroraBaseColors.cobalt_70,
  backgroundPhoto: auroraBaseColors.cobalt_60,
  backgroundTopMatch: auroraBaseColors.cobalt_65,
  backgroundModal: auroraBaseColors.cobalt_50,
  backgroundTransparent: auroraBaseColors.transparent,
  backgroundSurfaceElement: auroraBaseColors.white_opacity_10,
  backgroundDropdown: auroraBaseColors.cobalt_50,
  backgroundPrimaryDark: auroraBaseColors.cobalt_60,

  // Border
  borderDefault: auroraBaseColors.white_opacity_30,
  borderActive: auroraBaseColors.white_opacity_80,
  borderHover: auroraBaseColors.white_opacity_80,
  borderFocus: auroraBaseColors.white,
  borderInput: auroraBaseColors.white_opacity_60,
  borderTopMatch: auroraBaseColors.cobalt_50,
  borderSuccess: auroraBaseColors.green_20,
  borderError: auroraBaseColors.red_20,
  borderWarning: auroraBaseColors.gold_20,

  // Shadows - same as light mode

  // Icon
  iconDefault: auroraBaseColors.white,
  iconActive: auroraBaseColors.cobalt_25,
  iconSuccess: auroraBaseColors.green_30,
  iconWarning: auroraBaseColors.gold_30,
  iconError: auroraBaseColors.red_30,
  iconAccent: auroraBaseColors.gold_30,

  // Divider
  dividerPrimary: auroraBaseColors.white_opacity_50,
  dividerSecondary: auroraBaseColors.white_opacity_80,

  // Primary button
  primaryButtonBackgroundDefault: auroraBaseColors.cobalt_80,
  primaryButtonBackgroundHover: auroraBaseColors.cobalt_70,
  primaryButtonBackgroundPressed: auroraBaseColors.cobalt_70,
  primaryButtonBackgroundInactive: auroraBaseColors.button_background_inactive,
  primaryButtonBackgroundLoading: auroraBaseColors.cobalt_70,
  primaryButtonBackgroundFocus: auroraBaseColors.cobalt_70,
  primaryButtonTextActive: auroraBaseColors.white,
  primaryButtonTextInactive: auroraBaseColors.white_opacity_50,

  // Primary button variants

  // Secondary button
  secondaryButtonBackgroundDefault: auroraBaseColors.white_opacity_20,
  secondaryButtonBackgroundHover: auroraBaseColors.white_opacity_30,
  secondaryButtonBackgroundPressed: auroraBaseColors.white_opacity_30,
  secondaryButtonBackgroundInactive: auroraBaseColors.white_opacity_10,
  secondaryButtonBackgroundFocus: auroraBaseColors.white_opacity_30,
  secondaryButtonOutlineDefault: auroraBaseColors.white_opacity_20,
  secondaryButtonOutlineInactive: auroraBaseColors.transparent,
  secondaryButtonTextActive: auroraBaseColors.white,
  secondaryButtonTextInactive: auroraBaseColors.white_opacity_30,

  // Secondary button variants
  secondaryButtonInverseBackgroundDefault: auroraBaseColors.transparent,
  secondaryButtonInverseBackgroundHover: auroraBaseColors.blueberry_60,
  secondaryButtonInverseBackgroundPressed: auroraBaseColors.blueberry_50,
  secondaryButtonInverseBackgroundFocus: auroraBaseColors.blueberry_60,
  secondaryButtonInverseOutlineDefault: auroraBaseColors.blueberry_20,
  secondaryButtonInverseOutlinePressed: auroraBaseColors.blueberry_20,
  secondaryButtonInverseOutlineInactive: auroraBaseColors.oatmeal_20,
  secondaryButtonInverseTextActive: auroraBaseColors.blueberry_20,
  secondaryButtonInverseTextInactive: auroraBaseColors.oatmeal_20,

  // Tertiary button (icon button)
  tertiaryButtonBackgroundDefault: auroraBaseColors.transparent,
  tertiaryButtonBackgroundHover: auroraBaseColors.white_opacity_10,
  tertiaryButtonBackgroundPressed: auroraBaseColors.white_opacity_10,
  tertiaryButtonBackgroundInactive: auroraBaseColors.transparent,
  tertiaryButtonBackgroundFocus: auroraBaseColors.white_opacity_10,
  tertiaryButtonTextActive: auroraBaseColors.white,
  tertiaryButtonTextInactive: auroraBaseColors.white_opacity_30,

  // Secondary Tertiary button (icon button) tokens
  secondaryTertiaryButtonBackgroundDefault: auroraBaseColors.white_opacity_20,
  secondaryTertiaryButtonBackgroundHover: auroraBaseColors.white_opacity_30,
  secondaryTertiaryButtonBackgroundPressed: auroraBaseColors.white_opacity_30,
  secondaryTertiaryButtonBackgroundInactive: auroraBaseColors.transparent,
  secondaryTertiaryButtonBackgroundFocus: auroraBaseColors.white_opacity_30,

  // Secondary icon button tokens
  secondaryIconButtonBackgroundDefault: auroraBaseColors.white_opacity_20,
  secondaryIconButtonBackgroundHover: auroraBaseColors.white_opacity_30,
  secondaryIconButtonBackgroundPressed: auroraBaseColors.white_opacity_30,
  secondaryIconButtonBackgroundInactive: auroraBaseColors.transparent,
  secondaryIconButtonBackgroundFocus: auroraBaseColors.white_opacity_30,
  secondaryIconButtonOutlineDefault: auroraBaseColors.white_opacity_20,

  // White icon button tokens (dark theme)
  whiteIconButtonBackgroundDefault: auroraBaseColors.cobalt_75,
  whiteIconButtonBackgroundHover: auroraBaseColors.cobalt_65,
  whiteIconButtonBackgroundInactive: auroraBaseColors.cobalt_75,
  whiteIconButtonBackgroundFocus: auroraBaseColors.cobalt_65,

  // Teal icon button tokens
  tealIconButtonBackgroundDefault: auroraBaseColors.white_opacity_10,
  tealIconButtonBackgroundHover: auroraBaseColors.white_opacity_10,
  tealIconButtonBackgroundPressed: auroraBaseColors.cobalt_60,
  tealIconButtonBackgroundFocus: auroraBaseColors.cobalt_60,
  tealIconButtonIconDefault: auroraBaseColors.white,
  tealIconButtonIconHover: auroraBaseColors.white,
  tealIconButtonIconPressed: auroraBaseColors.white,
  tealIconButtonIconFocus: auroraBaseColors.white,

  // Primary icon button tokens
  primaryIconButtonBackgroundDefault: auroraBaseColors.transparent,
  primaryIconButtonBackgroundHover: auroraBaseColors.white_opacity_10,
  primaryIconButtonBackgroundPressed: auroraBaseColors.white_opacity_10,
  primaryIconButtonBackgroundFocus: auroraBaseColors.white_opacity_10,
  primaryIconButtonBackgroundInactive: auroraBaseColors.transparent,
  primaryIconButtonTextActive: auroraBaseColors.white,

  // Word cloud button
  wordCloudButtonBackgroundDefault: auroraBaseColors.white_opacity_10,
  wordCloudButtonBackgroundSelected: auroraBaseColors.white,
  wordCloudButtonBackgroundPressed: auroraBaseColors.white_opacity_20,
  wordCloudButtonOutlineDefault: auroraBaseColors.white_opacity_20,
  wordCloudButtonOutlineHover: auroraBaseColors.white_opacity_40,
  wordCloudButtonOutlinePressed: auroraBaseColors.white_opacity_40,
  wordCloudButtonOutlineInactive: auroraBaseColors.transparent,
  wordCloudButtonOutlineSelected: auroraBaseColors.white,
  wordCloudButtonTextActive: auroraBaseColors.white,
  wordCloudButtonTextInactive: auroraBaseColors.white_opacity_30,
  wordCloudButtonTextPressed: auroraBaseColors.white,
  wordCloudButtonTextSelected: auroraBaseColors.cobalt_80,

  // Input field
  inputOutlineDefault: auroraBaseColors.white_opacity_60,
  inputOutlineSelected: auroraBaseColors.white_opacity_80,
  inputOutlineInactive: auroraBaseColors.transparent,
  inputOutlineHover: auroraBaseColors.white,
  inputOutlineFocus: auroraBaseColors.white,
  inputOutlineError: auroraBaseColors.red_20,
  inputTextDefault: auroraBaseColors.white,
  inputTextPlaceholder: auroraBaseColors.cobalt_20,
  inputTextInactive: auroraBaseColors.white_opacity_30,
  inputTextSuccess: auroraBaseColors.green_20,
  inputTextWarning: auroraBaseColors.gold_20,
  inputTextError: auroraBaseColors.red_20,
  inputSelectionText: auroraBaseColors.cobalt_80,
  inputSelectionBackground: auroraBaseColors.white_opacity_80,
  inputBackgroundDefault: auroraBaseColors.transparent,
  inputBackgroundInactive: auroraBaseColors.white_opacity_10,
  inputBackgroundTriageDefault: auroraBaseColors.white_opacity_10,
  inputOutlineTriageDefault: auroraBaseColors.white_opacity_20,

  // Typeahead
  typeaheadMenuBackground: auroraBaseColors.cobalt_50,
  typeaheadItemBackgroundHover: auroraBaseColors.white_opacity_20,

  // Checkbox
  checkboxBackgroundPrimary: auroraBaseColors.transparent,
  checkboxBackgroundPrimarySelected: auroraBaseColors.white,
  checkboxTextDefault: auroraBaseColors.white,
  checkboxTextSelected: auroraBaseColors.cobalt_80,
  checkboxOutlineSelectedInactive: auroraBaseColors.cobalt_60,
  checkboxBackgroundSelected: auroraBaseColors.cobalt_80,
  checkboxBackgroundPressed: auroraBaseColors.white_opacity_20,
  checkboxOutlineSelected: auroraBaseColors.white,
  checkboxOutlineDefault: auroraBaseColors.white_opacity_20,
  checkboxOutlineHovered: auroraBaseColors.white_opacity_40,
  checkboxCheckSelected: auroraBaseColors.white,
  checkboxOutlineInactive: auroraBaseColors.white_opacity_30,

  // Radio
  radioBackgroundPrimary: auroraBaseColors.transparent,
  radioBackgroundSelected: auroraBaseColors.cobalt_80,
  radioBackgroundSelectedInactive: auroraBaseColors.cobalt_mid_opacity,
  radioTextDefault: auroraBaseColors.white,
  radioTextHighlight: auroraBaseColors.white,
  radioTextSelected: auroraBaseColors.cobalt_80,
  radioOutlineDefault: auroraBaseColors.white,
  radioOutlineHover: auroraBaseColors.white,
  radioOutlineInactive: auroraBaseColors.white_opacity_30,
  radioOutlineSelected: auroraBaseColors.cobalt_80,
  radioOutlineSelectedInactive: auroraBaseColors.cobalt_mid_opacity,
  radioOutlineFocused: auroraBaseColors.white,

  // Radio with outline
  radioWithOutlineBackgroundSelected: auroraBaseColors.white,
  radioWithOutlineBorderSelected: auroraBaseColors.white,
  radioWithOutlinePrimaryTextSelected: auroraBaseColors.cobalt_80,
  radioWithOutlineSecondaryTextSelected: auroraBaseColors.cobalt_80,
  radioWithOutlineBorderHovered: auroraBaseColors.white_opacity_40,
  radioWithOutlinePrimaryTextInactive: auroraBaseColors.white_opacity_30,
  radioWithOutlineSecondaryTextInactive: auroraBaseColors.white_opacity_30,
  radioWithOutlineBackgroundDefault: auroraBaseColors.white_opacity_10,
  radioWithOutlineBorderInactive: auroraBaseColors.transparent,
  radioWithOutlinePrimaryTextDefault: auroraBaseColors.white,
  radioWithOutlineSecondaryTextDefault: auroraBaseColors.white,
  radioWithOutlineBorderDefault: auroraBaseColors.white_opacity_20,
  radioWithOutlineBackgroundPressed: auroraBaseColors.white_opacity_20,

  // Cards
  cardBackgroundPrimary: auroraBaseColors.white_opacity_10,
  cardBackgroundSecondary: auroraBaseColors.white_opacity_10,
  cardOutlinePrimary: auroraBaseColors.transparent,
  cardOutlineInactive: auroraBaseColors.cobalt_65,
  topMatchCardGradientStart: auroraBaseColors.cobalt_80,
  topMatchCardGradientEnd: auroraBaseColors.cobalt_60,
  cardBoxShadow: auroraBaseColors.black_opacity_10,

  // Progress indicator
  progressIndicator: auroraBaseColors.white_opacity_80,
  progressIndicatorBackgroundTop: auroraBaseColors.white_opacity_20,
  progressIndicatorBackgroundBottom: auroraBaseColors.white_opacity_20,

  // ScrollView Refresher
  scrollViewRefresherLoadingIcon: auroraBaseColors.cobalt_40,

  // Gradient Tokens
  gradientBubbleStart: auroraBaseColors.cobalt_10,
  gradientBubbleEnd: auroraBaseColors.teal_0,
  gradientHeaderStart: auroraBaseColors.cobalt_70,
  gradientHeaderEnd: auroraBaseColors.header_dark_end,

  // Page background gradient tokens
  backgroundGradientFirst: auroraBaseColors.cobalt_70,
  backgroundGradientSecond: auroraBaseColors.cobalt_50,

  // Footer gradient tokens (opacity applied as: 0%, 90%, 100%)
  gradientBackgroundFooter: auroraBaseColors.cobalt_50,

  // Skeleton tokens
  skeletonBoneColor: auroraBaseColors.cobalt_60_white_opacity_50_blend,
  skeletonHighlightColor: auroraBaseColors.white_opacity_50,

  components: {
    ...auroraLightThemeColors.components,
    ...darkThemeColors.components,

    badge: {
      ...darkThemeColors.components.badge,
      success: {
        ...darkThemeColors.components.badge.success,
        background: auroraBaseColors.green_10,
        text: auroraBaseColors.green_45,
      },
      attention: {
        ...darkThemeColors.components.badge.attention,
        background: auroraBaseColors.gold_10,
        text: auroraBaseColors.gold_50,
      },
      alert: {
        ...darkThemeColors.components.badge.alert,
        background: auroraBaseColors.red_10,
        text: auroraBaseColors.red_50,
      },
      infoPrimary: {
        ...darkThemeColors.components.badge.infoPrimary,
        background: auroraBaseColors.cobalt_40,
        text: auroraBaseColors.white,
      },
      neutral: {
        background: auroraBaseColors.white_opacity_20,
        text: auroraBaseColors.white,
      },
      inverse: {
        ...darkThemeColors.components.badge.inverse,
        background: auroraBaseColors.cobalt_65,
        text: auroraBaseColors.cobalt_20,
      },
    },
    googlePlacesField: {
      textInput: {
        background: darkThemeColors.backgroundTransparent,
        text: darkThemeColors.textSecondary,
      },
      row: {
        background: auroraBaseColors.transparent,
        text: darkThemeColors.textSecondary,
      },
    },
    activityHeader: {
      headline: {
        border: auroraBaseColors.cobalt_40,
      },
    },
    alertBanner: {
      ...darkThemeColors.components.alertBanner,
      calm: { ...darkThemeColors.components.alertBanner.calm, background: auroraBaseColors.cobalt_10 },
      neutral: { background: auroraBaseColors.cobalt_80, text: auroraBaseColors.white },
    },
    avatar: {
      ...darkThemeColors.components.avatar,
      tealTheme: { background: auroraBaseColors.cobalt_60, text: auroraBaseColors.cobalt_30 },
      defaultAvatarTextColor: auroraBaseColors.cobalt_45,
    },
    careOptionsCarousel: {
      cardShadow: { fill: auroraBaseColors.cobalt_70 },
      cardShadowWithOpacity: { fill: auroraBaseColors.cobalt_70_opacity_20 },
      dotsPagination: {
        activeDot: {
          fill: auroraBaseColors.cobalt_20,
        },
        inactiveDot: {
          fill: auroraBaseColors.white_opacity_20,
        },
      },
    },
    chatBubble: {
      ...darkThemeColors.components.chatBubble,
      outOfOffice: {
        ...darkThemeColors.components.chatBubble.outOfOffice,
        background: auroraBaseColors.cobalt_30,
      },
    },
    coachMark: {
      background: auroraBaseColors.cobalt_10,
      arrow: {
        border: auroraBaseColors.cobalt_10,
      },
    },
    compactTile: {
      container: { background: auroraBaseColors.white_opacity_10 },
    },
    dateBlock: {
      border: auroraBaseColors.cobalt_30,
      text: auroraBaseColors.cobalt_30,
      background: auroraBaseColors.cobalt_60,
    },
    favoritedProvidersStarIcon: {
      filled: {
        fill: auroraBaseColors.white,
      },
      filledAlternate: {
        fill: auroraBaseColors.white,
      },
      unfilled: {
        fill: auroraBaseColors.white,
      },
    },
    favoritedProvidersRemainingDays: {
      warning: auroraBaseColors.gold_60,
    },
    feedbackStarIcon: {
      ...darkThemeColors.components.feedbackStarIcon,
      default: {
        ...darkThemeColors.components.feedbackStarIcon.default,
        hover: {
          ...darkThemeColors.components.feedbackStarIcon.default.hover,
          border: auroraBaseColors.cobalt_40,
        },
        highlighted: {
          fill: auroraBaseColors.cobalt_40,
          border: auroraBaseColors.cobalt_40,
        },
      },
    },
    floatingFooterBackground: {
      background: auroraBaseColors.white_opacity_10,
    },
    floatingFooterMobileBackground: {
      background: auroraBaseColors.white_opacity_10,
    },
    focusBoxShadow: {
      outerBox: { fill: auroraBaseColors.white },
      innerBox: { fill: auroraBaseColors.cobalt_65 },
    },
    genericHeader: {
      background: auroraBaseColors.cobalt_10,
    },
    lessonPreview: {
      ...darkThemeColors.components.lessonPreview,
      progressBar: {
        fill: auroraBaseColors.cobalt_30,
      },
    },
    lessonThumbnail: {
      iconDisplay: {
        background: auroraBaseColors.white,
        fill: auroraBaseColors.cobalt_50,
      },
      disabledIcon: {
        fill: auroraBaseColors.cobalt_40,
      },
      progressBar: {
        fill: auroraBaseColors.cobalt_30,
      },
    },
    locationSearchNavigation: {
      ...darkThemeColors.components.locationSearchNavigation,
      highlightColor: { background: auroraBaseColors.cobalt_40 },
      separatorColor: { background: auroraBaseColors.transparent },
      infoBadge: {
        background: auroraBaseColors.white_opacity_20,
        text: auroraBaseColors.white,
      },
    },
    onsiteLocation: {
      badge: {
        background: auroraBaseColors.cobalt_10,
        text: auroraBaseColors.cobalt_40,
      },
    },
    messageAttachment: {
      innerContainer: {
        background: auroraBaseColors.cobalt_20,
      },
    },
    messageItem: {
      ...darkThemeColors.components.messageItem,
      chatBubbleSender: {
        background: auroraBaseColors.cobalt_20,
        border: auroraBaseColors.cobalt_20,
      },
      chatBubbleAurora: {
        background: auroraBaseColors.white_opacity_10,
        text: auroraBaseColors.white,
      },
      chatBubbleReceiverAi: {
        ...darkThemeColors.components.messageItem.chatBubbleReceiverAi,
        gradientStart: { background: auroraBaseColors.white_opacity_10 },
        gradientEnd: { background: auroraBaseColors.white_opacity_10 },
        text: auroraBaseColors.white,
      },
    },
    chatInput: {
      ...darkThemeColors.components.chatInput,
      sendButtonBackgroundActive: auroraBaseColors.white_opacity_20,
      sendButtonBackgroundInactive: auroraBaseColors.white_opacity_10,
      sendButtonIconActive: auroraBaseColors.white,
      sendButtonIconInactive: auroraBaseColors.white_opacity_30,
    },
    notificationsOverview: {
      notificationIcon: {
        fill: auroraBaseColors.cobalt_10,
      },
    },
    passwordChecklist: {
      ...darkThemeColors.components.passwordChecklist,
      checklistBullet: {
        fill: auroraBaseColors.cobalt_10,
      },
    },
    platformEngagementQuarterlyDetails: {
      text: auroraBaseColors.cobalt_10,
    },
    privacyScreen: {
      textContainer: { background: auroraBaseColors.cobalt_60 },
    },
    profilePageCard: { border: auroraBaseColors.transparent },
    progressBar: {
      ...darkThemeColors.components.progressBar,
      fill: auroraBaseColors.cobalt_50,
    },
    progressBarSecondary: {
      fill: auroraBaseColors.cobalt_30,
      background: auroraBaseColors.cobalt_20,
    },
    heroSection: {
      background: {
        gradient: [auroraBaseColors.cobalt_70, auroraBaseColors.cobalt_50] as const,
      },
    },
    contentSection: {
      background: {
        gradient: [auroraBaseColors.cobalt_50, auroraBaseColors.cobalt_40] as const,
      },
    },
    providerLanguagesSpoken: {
      ...darkThemeColors.components.providerLanguagesSpoken,
      textBadge: {
        text: auroraBaseColors.cobalt_20,
      },
    },
    providerBadges: {
      ...darkThemeColors.components.providerBadges,
      bioKeywordBadge: {
        background: auroraBaseColors.white_opacity_20,
        text: auroraBaseColors.white,
      },
      preferenceMatchBadge: {
        checkIcon: { fill: auroraBaseColors.cobalt_40 },
        background: auroraBaseColors.white_opacity_20,
        popoverBackground: auroraBaseColors.cobalt_50,
      },
      memberRecommendedBadge: {
        background: auroraBaseColors.cobalt_60,
      },
      specializationBadge: {
        background: auroraBaseColors.white_opacity_20,
        text: auroraBaseColors.white,
      },
    },
    preferenceMatchCheckmark: {
      background: auroraBaseColors.cobalt_80,
      foreground: auroraBaseColors.white,
    },
    s4cLoadingIndicator: {
      text: auroraBaseColors.cobalt_10,
    },
    s4cTriageBackgroundHeader: {
      background: auroraBaseColors.cobalt_75,
    },
    sliderField: {
      text: auroraBaseColors.white,
      thumb: {
        border: auroraBaseColors.cobalt_50,
        background: auroraBaseColors.white,
        dashedOutline: {
          background: auroraBaseColors.white,
        },
      },
      track: {
        filled: {
          background: auroraBaseColors.oatmeal_30,
        },
        unFilled: {
          background: auroraBaseColors.oatmeal_40,
        },
        minimumTint: {
          background: auroraBaseColors.cobalt_40,
        },
        maximumTint: {
          background: auroraBaseColors.white_opacity_20,
        },
      },
      stepMarker: {
        filled: {
          background: auroraBaseColors.white,
        },
        active: {
          background: auroraBaseColors.white,
        },
        unFilled: {
          background: auroraBaseColors.cobalt_65,
        },
      },
      floatingLabel: {
        border: auroraBaseColors.white_opacity_20,
        background: auroraBaseColors.white_opacity_10,
        shadow: { fill: 'transparent' },
      },
    },
    triageSummary: {
      ...darkThemeColors.components.triageSummary,
      badge: {
        background: auroraBaseColors.cobalt_60,
        fill: auroraBaseColors.cobalt_30,
      },
    },
    toolbar: {
      background: auroraBaseColors.cobalt_10,
    },
    toolkitPage: {
      headerBackground: { background: auroraBaseColors.cobalt_50 },
    },
    treatmentCard: {
      background: auroraBaseColors.white_opacity_10,
      findCounsellorSection: {
        icon: {
          fill: auroraBaseColors.cobalt_60,
        },
      },
      headlineUnderline: {
        background: auroraBaseColors.white_opacity_50,
      },
    },
    unsupportedLanguageDisclaimer: {
      text: auroraBaseColors.cobalt_20,
    },
    videoLessonComplete: {
      ...darkThemeColors.components.videoLessonComplete,
      check: { fill: auroraBaseColors.cobalt_20 },
      completeText: { text: auroraBaseColors.cobalt_20 },
    },
    videoPlayer: {
      ...darkThemeColors.components.videoPlayer,
      minimumTrackTint: { background: auroraBaseColors.cobalt_20 },
    },
    welcomeSplashScreen: {
      backButton: { fill: auroraBaseColors.cobalt_30 },
    },
    wellnessToolkitEssentials: {
      text: auroraBaseColors.white,
    },
    treatmentRecommendation: {
      background: auroraBaseColors.transparent,
    },
    bottomTabBar: {
      background: auroraBaseColors.cobalt_75,
    },
    twoColumnRegistrationLayout: {
      formBackground: {
        background: auroraBaseColors.cobalt_75,
      },
      ssoDivider: {
        background: auroraBaseColors.cobalt_mid_opacity,
      },
    },
    inlineInnovationBanner: {
      background: auroraBaseColors.cobalt_50,
      text: auroraBaseColors.white,
    },
    calendarNotificationBanner: {
      background: auroraBaseColors.cobalt_40,
    },
    internationalPhoneNumberEntry: {
      countryButtonAndPhoneInputBox: {
        background: auroraBaseColors.cobalt_75,
      },
    },
    setupAccountForm: {
      submitButtonContainer: {
        background: auroraBaseColors.cobalt_75,
      },
      formBodyPageContainer: {
        background: auroraBaseColors.cobalt_75,
      },
    },
    wellnessCheckInResultsEssentialsCard: {
      background: auroraBaseColors.cobalt_70,
    },
    careNavigatorContactCard: {
      container: { background: auroraBaseColors.white_opacity_10 },
    },
    neurodiversityToolkit: {
      ...darkThemeColors.components.neurodiversityToolkit,
      howItWorksIllustration: {
        background: auroraBaseColors.cobalt_80,
      },
    },
    careNavigatorCard: {
      container: {
        background: auroraBaseColors.white_opacity_10,
      },
      phoneNumbersContainer: {
        background: auroraBaseColors.cobalt_60,
      },
    },
    centerOfExcellenceEntryPointBackground: { background: auroraBaseColors.cobalt_60 },
    onboardingNavBarDefaultBackground: {
      background: auroraBaseColors.cobalt_75,
    },
    searchBar: {
      container: { background: auroraBaseColors.white_opacity_20 },
    },
    floatingMobileNav: {
      background: auroraBaseColors.cobalt_70_opacity_80,
      iconActive: auroraBaseColors.cobalt_25,
      iconDefault: auroraBaseColors.white,
      activeIndicator: auroraBaseColors.cobalt_25,
    },
    languageSelector: {
      button: { background: auroraBaseColors.white_opacity_20, border: auroraBaseColors.white_opacity_20 },
    },
    segmentedToggle: {
      container: { background: auroraBaseColors.white_opacity_10, border: auroraBaseColors.white_opacity_20 },
      option: { text: auroraBaseColors.white },
      optionHovered: { background: auroraBaseColors.white_opacity_20 },
      optionSelected: {
        background: auroraBaseColors.white,
        border: auroraBaseColors.white_opacity_20,
        text: auroraBaseColors.cobalt_80,
      },
    },
    chatHeader: {
      ...darkThemeColors.components.chatHeader,
      background: { background: auroraBaseColors.transparent },
      backgroundScrolled: { background: auroraBaseColors.cobalt_65_opacity_60 },
    },
    careNavigatorHeaderEntryPointButton: {
      iconCircle: { background: auroraBaseColors.white_opacity_20, border: auroraBaseColors.white_opacity_20 },
      icon: { fill: auroraBaseColors.white },
      heart: { fill: auroraBaseColors.white },
    },
    pathwaysFAQSection: {
      overlay: { background: auroraBaseColors.white_opacity_20 },
    },
    careOptionCard: {
      container: { background: auroraBaseColors.cobalt_50 },
    },
    zoomVideoControls: {
      ...darkThemeColors.components.zoomVideoControls,
      disabled: { background: auroraBaseColors.cobalt_40 },
      end: { background: baseColors.red_50 },
    },
  },

  icons: {
    ...auroraLightThemeColors.icons,
    ...darkThemeColors.icons,

    avatarIcon: {
      body: { fill: auroraBaseColors.cobalt_60 },
      shadow: { fill: auroraBaseColors.cobalt_50 },
      fill: auroraBaseColors.cobalt_40,
    },
    checkMarkIcon: {
      fill: auroraBaseColors.white,
    },
    creditCardIcon: {
      fill: auroraBaseColors.white,
    },
    essentialsIcon: {
      fill: auroraBaseColors.cobalt_10,
    },
    eyeIcon: {
      fill: auroraBaseColors.cobalt_10,
    },
    fingerprintIcon: {
      fill: auroraBaseColors.cobalt_50,
    },
    linkIcon: {
      chain: { fill: auroraBaseColors.cobalt_40 },
    },
    logoutIcon: {
      rectangle: { fill: auroraBaseColors.cobalt_50 },
      arrow: { fill: auroraBaseColors.cobalt_30 },
    },
    lyraLogoPrimaryIcon: {
      fill: auroraBaseColors.white, // TODO: Check for regresssions
    },
    lyraLogoAppPromoIcon: {
      fill: auroraBaseColors.cobalt_30,
    },
    lyraHealthAppIcon: {
      background: { fill: auroraBaseColors.cobalt_30 },
      logo: { fill: auroraBaseColors.cobalt_80 },
    },
    menuIcon: {
      fill: auroraBaseColors.cobalt_10,
    },
    messagingIcon: {
      border: auroraBaseColors.cobalt_10,
    },
    notificationIcon: {
      fill: auroraBaseColors.cobalt_40,
    },
    progressIcon: {
      fill: auroraBaseColors.cobalt_10,
    },
    scoringIcon: {
      ...darkThemeColors.icons.scoringIcon,
      neutral: {
        arrow: { fill: auroraBaseColors.cobalt_30 },
        stroke: { fill: auroraBaseColors.cobalt_50 },
        fill: auroraBaseColors.cobalt_60,
      },
    },
    selfCareIcon: {
      ...darkThemeColors.icons.selfCareIcon,
      fill: auroraBaseColors.cobalt_40,
    },
    sessionIcon: {
      fill: auroraBaseColors.cobalt_10,
    },
    timeIcon: {
      background: auroraBaseColors.cobalt_60,
      clockHandBackground: {
        fill: auroraBaseColors.cobalt_50,
      },
      clockHandForeground: {
        fill: auroraBaseColors.cobalt_40,
      },
    },
    xIcon: {
      fill: auroraBaseColors.white,
    },
    spotIllustration: {
      primary: { fill: auroraBaseColors.blue_40 },
      secondary: { fill: auroraBaseColors.blue_50 },
      background: { fill: auroraBaseColors.white_opacity_20 },
      orbGradientStart: { fill: auroraBaseColors.yellow_40 },
      orbGradientEnd: { fill: auroraBaseColors.orange_50 },
    },
  },
}
